import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import { quantileLabel } from '@/lib/quantiles';

import type { ConditionalFines } from '@/services/types';

/**
 * The selected population's quantiles against the corpus, on a log axis.
 *
 * Both series are drawn together because the comparison IS the finding. Shown
 * alone, a population median of R$2,392 looks like a fact about that citation;
 * beside the corpus median of R$2,392 it is visibly nothing at all. The measured
 * pattern in this corpus is that citations separate the upper tail while the
 * median barely moves -- the backend computes and states that when it holds.
 */
export function ConditionalFinesChart({ value }: { value: ConditionalFines }) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const compact = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const exact = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const ratio = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });

  const data = Object.keys(value.population.quantiles).map((key) => ({
    // Labelled, not raw: "p90" on an axis is analyst notation and the reader of
    // this screen is a lawyer deciding whether a fine is out of line.
    label: quantileLabel(t, key),
    selection: value.population.quantiles[key],
    corpus: value.corpus.quantiles[key],
  }));

  return (
    <div className="space-y-4">
      {value.subject_percentile !== null && value.subject_amount !== null ? (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
          <p className="text-sm text-foreground">
            {t('anatel:sanctions.placement', {
              amount: exact.format(value.subject_amount),
              percentile: percent.format(value.subject_percentile),
              n: value.population.n,
            })}
          </p>
        </div>
      ) : null}

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis
              scale="log"
              domain={['auto', 'auto']}
              allowDataOverflow
              tickFormatter={(v: number) => compact.format(v)}
              tick={{ fontSize: 11 }}
              width={72}
              className="fill-muted-foreground"
            />
            <Tooltip
              formatter={(v: number) => exact.format(v)}
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
            <Bar
              dataKey="selection"
              name={t('anatel:sanctions.selection')}
              fill="hsl(var(--primary))"
              radius={[2, 2, 0, 0]}
            />
            <Bar
              dataKey="corpus"
              name={t('anatel:sanctions.corpus')}
              fill="hsl(var(--muted-foreground))"
              fillOpacity={0.35}
              radius={[2, 2, 0, 0]}
            />
            {value.subject_amount ? (
              <ReferenceLine
                y={value.subject_amount}
                stroke="hsl(var(--primary))"
                strokeDasharray="4 3"
              />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Ratio label={t('anatel:sanctions.medianRatio')} value={`${ratio.format(value.median_ratio)}x`} />
        <Ratio label={t('anatel:sanctions.p90Ratio')} value={`${ratio.format(value.p90_ratio)}x`} />
        <Ratio
          label={t('anatel:sanctions.selectionN')}
          value={new Intl.NumberFormat(i18n.language).format(value.population.n)}
        />
        <Ratio
          label={t('anatel:sanctions.corpusN')}
          value={new Intl.NumberFormat(i18n.language).format(value.corpus.n)}
        />
      </dl>
    </div>
  );
}

function Ratio({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}
